import React, { useState, useEffect } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/analytics";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useNavigate, useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";

import useSharedVideos from "../../../hooks/useSharedVideos";
import useOrganizationUsers from "../../../hooks/useOrganizationUsers";
import useProcessedVideosPaginated from "../../../hooks/useProcessedVideosPaginated";
import useProcessedVideosCount from "../../../hooks/useProcessedVideosCount";
import useEvents from "../../../hooks/useEvents";
import useRecentOrgProgressPhotoGroups from "../../../hooks/useRecentOrgProgressPhotoGroups";
import MediaProgressPhotosClientSummaryList from "./shared-videos/components/MediaProgressPhotosClientSummaryList";

import LoadingState from "./shared-videos/components/LoadingState";
import VideoStatisticsCard from "./shared-videos/components/VideoStatisticsCard";
import VideoGallery from "./shared-videos/components/VideoGallery";
import ProcessedVideosTable from "./shared-videos/components/ProcessedVideosTable";
import VideoViewerDialog from "./shared-videos/components/VideoViewerDialog";
import SendMessageDialog from "../../../components/organisms/SendMessageDialog";
import useThumbnailGenerator from "./shared-videos/components/ThumbnailGenerator";
import PageHeader from "../../../components/PageHeader";

import {
  extractAllVideos,
  getMostRecentVideo,
} from "./shared-videos/components/VideoUtils";

import organizationIdState from "../../../atoms/organizationIdAtom";

/**
 * Media home: Videos + Progress Photos summary list (detail is a nested route).
 */
const SharedVideoMediaMain = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const events = useEvents();
  const confirm = useConfirm();

  const organizationId = useRecoilValue(organizationIdState);

  const [selectedVideo, setSelectedVideo] = useState(null);
  const [openViewer, setOpenViewer] = useState(false);
  const [sortMode, setSortMode] = useState("unprocessed");
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [sendMessageOpen, setSendMessageOpen] = useState(false);
  const [viewMode, setViewMode] = useState("unprocessed");
  const [mainTab, setMainTab] = useState("videos");

  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  React.useEffect(() => {
    if (location.state?.mediaTab === "progressPhotos") {
      setMainTab("progressPhotos");
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {},
      });
    }
  }, [location.state?.mediaTab, location.pathname, location.search, navigate]);

  const users = useOrganizationUsers({
    organizationId: organizationId,
  });

  const loadVideosTab = mainTab === "videos";
  const loadProgressPhotosTab = mainTab === "progressPhotos";

  const {
    clientSections: progressPhotoClientSections,
    loading: loadingRecentPhotoGroups,
    error: recentPhotoGroupsError,
  } = useRecentOrgProgressPhotoGroups(
    loadProgressPhotosTab ? organizationId : null,
    {
      maxDocs: 320,
      maxClients: 18,
    },
  );

  const getClientDisplayName = React.useCallback(
    (uid) => {
      const u = users?.find((x) => x.uid === uid || x.id === uid);
      if (u?.firstName || u?.lastName) {
        return `${u.firstName || ""} ${u.lastName || ""}`.trim();
      }
      return uid;
    },
    [users],
  );

  const { count: processedVideosCount } = useProcessedVideosCount(
    loadVideosTab ? organizationId : null,
    { enabled: loadVideosTab },
  );

  const {
    videos: processedVideosData,
    loading: loadingProcessedVideos,
    error: processedVideosError,
    pagination: processedVideosPagination,
    refetch: refetchProcessedVideos,
  } = useProcessedVideosPaginated({
    organizationId:
      loadVideosTab && viewMode === "processed" ? organizationId : null,
    currentPage,
    pageSize,
    orderBy: "created_on",
    orderDirection: "desc",
  });

  const {
    videos,
    loading,
    loadingClients,
    isRefreshing,
    hasMore: hasMorePendingVideos,
    setVideoProcessedState,
    setLastUpdated,
  } = useSharedVideos({
    showAll: false,
    coachUID: localStorage.getItem("uid"),
    organizationId: organizationId,
    enabled: loadVideosTab,
  });

  const allVideos = React.useMemo(() => extractAllVideos(videos), [videos]);

  const processedVideos = React.useMemo(
    () => allVideos.filter((v) => v.processed),
    [allVideos],
  );
  const unprocessedVideos = React.useMemo(
    () => allVideos.filter((v) => !v.processed),
    [allVideos],
  );
  const exerciseVideos = React.useMemo(
    () => allVideos.filter((v) => v.metadata?.exercise),
    [allVideos],
  );
  const generalVideos = React.useMemo(
    () => allVideos.filter((v) => !v.metadata?.exercise),
    [allVideos],
  );
  const mostRecentVideo = React.useMemo(
    () => getMostRecentVideo(allVideos),
    [allVideos],
  );

  // Only generate thumbnails while Videos tab is active (avoids Storage noise on Progress Photos)
  const videosForThumbnails = React.useMemo(
    () => (mainTab === "videos" ? allVideos : []),
    [mainTab, allVideos],
  );

  const { thumbnails, loadingThumbnails, generateThumbnails } =
    useThumbnailGenerator(videosForThumbnails, {
      organizationId,
    });

  useEffect(() => {
    return () => {
      if (navigate.action === "POP") {
        navigate("/home");
      }
    };
  }, [navigate]);

  useEffect(() => {
    firebase.analytics().logEvent("page_view", {
      page_title: "Media Page",
      page_location: "/shared-video",
    });
  }, []);

  const handleRefreshVideos = () => {
    events.logButtonClick({
      buttonName: "Refresh Videos",
      pageName: "Shared Videos",
    });

    const triggerRefresh = async () => {
      try {
        if (organizationId) {
          const refreshMarkerRef = firebase
            .firestore()
            .collection("organizations")
            .doc(organizationId)
            .collection("refresh_markers")
            .doc("all_shared_videos");

          await refreshMarkerRef.set(
            {
              lastRefreshed: firebase.firestore.FieldValue.serverTimestamp(),
              refreshedBy: localStorage.getItem("uid") || "unknown",
            },
            { merge: true },
          );

          setLastUpdated(new Date());
        }
      } catch (error) {
        console.error("Error refreshing videos:", error);
      }
    };

    triggerRefresh();
  };

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    setOpenViewer(true);

    events.logButtonClick({
      buttonName: "View Shared Video",
      pageName: "Shared Videos",
    });
  };

  const handleToggleProcessed = async (video, feedback) => {
    if (!video) return;

    try {
      await setVideoProcessedState({
        id: video.id,
        feedback: feedback,
        uid: video.clientId,
        coachUID: localStorage.getItem("uid"),
        processed: !video.processed,
      });

      events.logButtonClick({
        buttonName: video.processed
          ? "Mark Video Unprocessed"
          : "Mark Video Processed",
        pageName: "Shared Videos",
      });

      setLastUpdated(new Date());
      setOpenViewer(false);
    } catch (error) {
      console.error("Error toggling video processed state:", error);
    }
  };

  const handleProcessAllClientVideos = async (clientUID, clientVideos) => {
    confirm({
      title: "Mark all videos as processed?",
      description:
        "This will mark all videos as processed and remove them from the list. You can view them again in the Client Summary. Continue?",
    })
      .then(async () => {
        const promises = clientVideos.map((video) => {
          return setVideoProcessedState({
            id: video.id,
            feedback: video.feedback || "",
            uid: video.clientId,
            coachUID: localStorage.getItem("uid"),
            processed: true,
          });
        });
        await Promise.all(promises);
        setLastUpdated(new Date());
        events.logButtonClick({
          buttonName: "Process All Client Videos",
          pageName: "Shared Videos",
        });
      })
      .catch(() => {});
  };

  const handleSendMessage = (clientId) => {
    setSelectedClientId(clientId);
    setSendMessageOpen(true);
  };

  const videosTabLoading = loading && !isRefreshing;

  const handleSeeProcessedVideos = () => {
    setViewMode("processed");
  };

  const handleProcessedVideoPlay = (video) => {
    setSelectedVideo(video);
    setOpenViewer(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  return (
    <>
      <PageHeader
        title="Media"
        subtitle="Review shared client videos and progress photo submissions"
      />
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            mb: 2,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Tabs
            value={mainTab}
            onChange={(event, newValue) => setMainTab(newValue)}
            aria-label="Media primary tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: { xs: 44, sm: 48 },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: { xs: "0.875rem", sm: "1rem" },
                minWidth: { xs: "auto", sm: 120 },
                px: { xs: 1.5, sm: 2 },
              },
            }}
          >
            <Tab label="Videos" value="videos" />
            <Tab label="Progress Photos" value="progressPhotos" />
          </Tabs>
        </Box>

        {mainTab === "videos" ? (
          videosTabLoading ? (
            <LoadingState title="Loading videos…" />
          ) : (
            <>
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  mb: 2,
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <Tabs
                  value={viewMode}
                  onChange={(event, newValue) => setViewMode(newValue)}
                  aria-label="Video queue tabs"
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    "& .MuiTab-root": {
                      fontSize: { xs: "0.8rem", sm: "0.875rem" },
                      minWidth: { xs: "auto", sm: 100 },
                      px: { xs: 1.25, sm: 2 },
                    },
                  }}
                >
                  <Tab
                    label={`Pending (${unprocessedVideos.length})`}
                    value="unprocessed"
                  />
                  <Tab
                    label={`Previous (${processedVideosPagination?.totalVideos || processedVideosCount})`}
                    value="processed"
                  />
                </Tabs>
              </Box>

              {viewMode === "unprocessed" ? (
                <>
                  {hasMorePendingVideos ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Showing the newest 100 pending videos. Process some to load
                      older ones on refresh.
                    </Alert>
                  ) : null}
                  <Box sx={{ mb: 2 }}>
                    <VideoStatisticsCard
                      allVideos={allVideos}
                      unprocessedVideos={unprocessedVideos}
                      processedVideos={processedVideos}
                      exerciseVideos={exerciseVideos}
                      generalVideos={generalVideos}
                      mostRecentVideo={mostRecentVideo}
                      onRefreshThumbnails={generateThumbnails}
                      onRefreshVideos={handleRefreshVideos}
                      loadingThumbnails={loadingThumbnails}
                      isRefreshing={isRefreshing}
                      loading={loading}
                      loadingClients={loadingClients}
                    />
                  </Box>

                  <Box>
                    <VideoGallery
                      videos={videos}
                      unprocessedVideos={unprocessedVideos}
                      sortMode={sortMode}
                      onSortModeChange={setSortMode}
                      onVideoClick={handleVideoClick}
                      thumbnails={thumbnails}
                      users={users}
                      onProcessAllClientVideos={handleProcessAllClientVideos}
                      onSendMessage={handleSendMessage}
                      events={events}
                      onRefreshVideos={handleRefreshVideos}
                      onSeeProcessedVideos={handleSeeProcessedVideos}
                      allVideos={allVideos}
                    />
                  </Box>
                </>
              ) : (
                <Box>
                  <ProcessedVideosTable
                    videos={processedVideosData}
                    loading={loadingProcessedVideos}
                    pagination={processedVideosPagination}
                    pageSize={pageSize}
                    onVideoPlay={handleProcessedVideoPlay}
                    onRefresh={refetchProcessedVideos}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </Box>
              )}
            </>
          )
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Clients with recent photo activity (from the latest org snapshot).
              Click a client for the full view: compare groups, download all, and
              AI analysis. Coach notes stay in sync everywhere.
            </Typography>
            {recentPhotoGroupsError ? (
              <Alert severity="warning">{recentPhotoGroupsError}</Alert>
            ) : loadingRecentPhotoGroups ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={32} />
              </Box>
            ) : progressPhotoClientSections.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No recent progress photos.
              </Typography>
            ) : (
              <MediaProgressPhotosClientSummaryList
                sections={progressPhotoClientSections}
                getClientDisplayName={getClientDisplayName}
              />
            )}
          </Box>
        )}
      </Box>

      <VideoViewerDialog
        open={openViewer}
        onClose={() => setOpenViewer(false)}
        video={selectedVideo}
        onProcessVideo={handleToggleProcessed}
        thumbnail={selectedVideo ? thumbnails[selectedVideo.id] : null}
      />

      {selectedClientId && sendMessageOpen && (
        <SendMessageDialog
          coachId={localStorage.getItem("uid")}
          uid={selectedClientId}
          open={sendMessageOpen}
          onDialogClosed={() => {
            setSendMessageOpen(false);
            setSelectedClientId(null);
          }}
        />
      )}
    </>
  );
};

export default SharedVideoMediaMain;
